import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Button, Drawer, Form, Input, Modal, Select, Tag, Upload, message } from "antd";
import {
  ApartmentOutlined,
  PaperClipOutlined,
  SendOutlined,
} from "@ant-design/icons";

import { CustomerTicketApi, ProjectApi, UserApi } from "../../services";
import AvatarSelect from "../AvatarSelect";
import ModalTitle from "../ModalTitle";
import { toFa } from "../../utils/helpers";
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
} from "../../pages/Customers/constants";
import styles from "./styles.module.css";

/**
 * یک تیکت مشتری، هرجا که بازش کنی.
 *
 * Shared rather than living inside the customer page, because a ticket has two
 * doors: the customer's own tab, and the queue of everything assigned to you.
 * Whoever a ticket is assigned to may not hold `manage_customers` at all — they
 * cannot open the customer page — so the queue has to be able to show the whole
 * thread itself instead of handing off to a route they are not allowed on.
 */
const jalali = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const CustomerTicketDrawer = ({ uuid, open, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm] = Form.useForm();

  const detail = useQuery({
    queryKey: ["[Single] Customer Ticket", uuid],
    queryFn: () =>
      CustomerTicketApi.getCustomerTicket({ params: { uuid } }).then(
        (response) => response.data.ticket,
      ),
    enabled: Boolean(uuid) && open,
  });

  const users = useQuery({
    queryKey: ["[Dropdown] Customer Owners"],
    queryFn: () =>
      UserApi.getUsers({ params: { mode: "active", per_page: 200 } }).then((response) =>
        (response.data.users || []).map((user) => ({
          value: user.id,
          label:
            user.full_name ||
            `${user.first_name || ""} ${user.last_name || ""}`.trim(),
          avatar: user.avatar?.original_url,
        })),
      ),
    initialData: [],
  });

  const projects = useQuery({
    queryKey: ["[Dropdown] Convert Ticket Projects"],
    queryFn: () =>
      ProjectApi.getProjects({ params: { per_page: 100 } }).then((response) =>
        /* پروژه بدون لوگو در لیست، همان پروژه‌ای است که کاربر باید اسمش را
           بخواند تا بشناسدش — بقیه‌ی پیکرهای پنل تصویر دارند. */
        (response.data.projects || []).map((project) => ({
          value: project.uuid,
          label: project.title,
          avatar: project.main_image?.original_url,
        })),
      ),
    initialData: [],
    enabled: convertOpen,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["[Single] Customer Ticket", uuid] });
    onChanged?.();
  };

  const fail = (fallback) => (error) =>
    message.error({ content: error?.response?.data?.message || fallback });

  const update = useMutation(CustomerTicketApi.updateCustomerTicket, {
    onSuccess: refresh,
    onError: fail("به‌روزرسانی تیکت انجام نشد"),
  });

  const reply = useMutation(CustomerTicketApi.replyCustomerTicket, {
    onSuccess: () => {
      setReplyText("");
      setReplyFiles([]);
      refresh();
    },
    onError: fail("ثبت پاسخ انجام نشد"),
  });

  const convert = useMutation(CustomerTicketApi.convertCustomerTicket, {
    onSuccess: () => {
      message.success({ content: "تسک ساخته شد و به تیکت وصل شد" });
      setConvertOpen(false);
      refresh();
    },
    onError: fail("تبدیل به تسک انجام نشد"),
  });

  const patch = (body) => update.mutate({ params: { uuid }, body });

  const submitReply = () => {
    const text = replyText.trim();
    if (!text) return message.warning({ content: "متن پاسخ خالی است" });

    const payload = new FormData();
    payload.append("body", text);
    replyFiles.forEach((file) => payload.append("attachments[]", file));

    return reply.mutate({ params: { uuid }, body: payload });
  };

  const ticket = detail.data;

  const attachments = (files) =>
    files?.length > 0 && (
      <div className={styles.files}>
        {files.map((file) => (
          <a key={file.uuid} href={file.original_url} target="_blank" rel="noreferrer">
            <PaperClipOutlined />
            {file.name}
          </a>
        ))}
      </div>
    );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={640}
        title={ticket?.subject || "تیکت"}
        extra={
          ticket && (
            <Button
              icon={<ApartmentOutlined />}
              disabled={Boolean(ticket.task)}
              onClick={() => {
                convertForm.resetFields();
                setConvertOpen(true);
              }}
            >
              {ticket.task ? "تسک ساخته شده" : "تبدیل به تسک"}
            </Button>
          )
        }
      >
        {ticket && (
          <>
            <div className={styles.customerLine}>
              {ticket.customer?.title}
              {ticket.contact?.name && ` — ${ticket.contact.name}`}
              {ticket.task && (
                <Tag color="blue" className="ms-2">
                  تسک: {ticket.task.title}
                </Tag>
              )}
            </div>

            <div className={styles.controls}>
              <div>
                <span className={styles.label}>وضعیت</span>
                <Select
                  value={ticket.status}
                  options={TICKET_STATUS_OPTIONS}
                  onChange={(value) => patch({ status: value })}
                />
              </div>
              <div>
                <span className={styles.label}>اولویت</span>
                <Select
                  value={ticket.priority}
                  options={TICKET_PRIORITY_OPTIONS}
                  onChange={(value) => patch({ priority: value })}
                />
              </div>
              <div>
                <span className={styles.label}>مسئول پیگیری</span>
                <AvatarSelect
                  bordered
                  allowClear
                  showSearch
                  className="w-100"
                  placeholder="بدون مسئول"
                  value={ticket.assigned_to}
                  options={users.data}
                  onChange={(value) => patch({ assigned_to: value ?? null })}
                />
              </div>
            </div>

            <div className={styles.thread}>
              <div className={styles.reply}>
                <div className={styles.replyHead}>
                  <strong>
                    {ticket.contact?.name || ticket.customer?.title || "مشتری"}
                  </strong>
                  <span>{jalali(ticket.created_at)}</span>
                </div>
                <div className={styles.replyBody}>
                  {ticket.body || "شرحی ثبت نشده است."}
                </div>
                {attachments(ticket.attachments)}
              </div>

              {(ticket.replies || []).map((row) => (
                <div className={styles.reply} key={row.id}>
                  <div className={styles.replyHead}>
                    <strong>{row.user?.full_name || row.contact?.name || "—"}</strong>
                    <span>{jalali(row.created_at)}</span>
                  </div>
                  <div className={styles.replyBody}>{row.body}</div>
                  {attachments(row.attachments)}
                </div>
              ))}
            </div>

            {ticket.status === "closed" ? (
              <Button
                className="mt-3"
                block
                onClick={() => patch({ status: "in_progress" })}
              >
                باز کردن دوباره
              </Button>
            ) : (
              <div className="mt-3 d-flex flex-column gap-2">
                <Input.TextArea
                  rows={3}
                  value={replyText}
                  placeholder="پاسخ شما"
                  onChange={(event) => setReplyText(event.target.value)}
                />
                <div className="d-flex align-items-center gap-2">
                  <Upload
                    multiple
                    showUploadList={false}
                    beforeUpload={(file) => {
                      setReplyFiles((prev) => [...prev, file]);
                      return false;
                    }}
                  >
                    <Button size="small" icon={<PaperClipOutlined />}>
                      پیوست
                    </Button>
                  </Upload>
                  {replyFiles.length > 0 && (
                    <span className={styles.label}>
                      {toFa(replyFiles.length)} فایل
                    </span>
                  )}
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={reply.isLoading}
                    onClick={submitReply}
                    style={{ marginInlineStart: "auto" }}
                  >
                    ثبت پاسخ
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Drawer>

      <Modal
        open={convertOpen}
        title={
          <ModalTitle
            icon={<ApartmentOutlined />}
            tone="blue"
            title="تبدیل تیکت به تسک"
            subtitle="تسک در پروژه ساخته می‌شود و به همین مشتری وصل می‌ماند"
          />
        }
        className="ds-modal"
        centered
        width={480}
        okText="ساخت تسک"
        cancelText="انصراف"
        confirmLoading={convert.isLoading}
        onOk={() => convertForm.submit()}
        onCancel={() => setConvertOpen(false)}
        destroyOnClose
      >
        <Form
          form={convertForm}
          layout="vertical"
          onFinish={(values) => convert.mutate({ params: { uuid }, body: values })}
          initialValues={{ title: ticket?.subject }}
        >
          <Form.Item
            name="project_uuid"
            label="پروژه"
            rules={[{ required: true, message: "پروژه را انتخاب کنید" }]}
          >
            <AvatarSelect
              bordered
              showSearch
              className="w-100"
              size="large"
              options={projects.data}
              placeholder="تسک در کدام پروژه ساخته شود؟"
            />
          </Form.Item>
          <Form.Item name="title" label="عنوان تسک">
            <Input placeholder="خالی بگذارید تا موضوع تیکت استفاده شود" />
          </Form.Item>
          <Form.Item name="users" label="مسئول تسک">
            <AvatarSelect
              bordered
              mode="multiple"
              allowClear
              showSearch
              className="w-100"
              options={users.data}
              placeholder="اختیاری"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerTicketDrawer;
